import { useEffect, useState } from "react";
import axios from "axios";
import { Hourglass } from "lucide-react";
import { useApp } from "@/App";

// Shows a friendly notice when the server is overloaded/slow instead of raw errors.
// Only real overload signals count (gateway errors, timeouts, very slow responses).
// Feature limits (429 login lock, AI quota, …) are not overload and show their own message.
// A single hiccup is ignored – the notice appears after 2 signals within 20 s.
const SLOW_MS = 6000;
const BUSY_STATUSES = new Set([502, 503, 504]);
const listeners = new Set();
let busyUntil = 0;
let strikes = [];
let resumedAt = 0;
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => { if (!document.hidden) resumedAt = Date.now(); });
}

const markBusy = () => {
  const now = Date.now();
  strikes = strikes.filter((t) => now - t < 20000).concat(now);
  if (strikes.length < 2) return;
  busyUntil = now + 15000;
  listeners.forEach((fn) => fn(true));
};

// Requests that die while the tab sleeps or right after waking up are not overload.
const justResumed = () => document.hidden || Date.now() - resumedAt < 5000;

const isBusyError = (err) => !err.response
  ? err.code !== "ERR_CANCELED" && navigator.onLine !== false && !justResumed()
  : BUSY_STATUSES.has(err.response.status);

// Slow by nature (AI, translations, files, syncs) – must not trigger the overload notice.
const SLOW_OK = /translate|generate|\/ai\/|upload|pdf|export|backup|sync|ical|import|csv|image|download/i;

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

let installed = false;
export const installBusyInterceptor = () => {
  if (installed) return;
  installed = true;
  axios.interceptors.request.use((cfg) => { cfg.metadata = { start: Date.now() }; return cfg; });
  axios.interceptors.response.use(
    (res) => {
      if (Date.now() - (res.config.metadata?.start || Date.now()) > SLOW_MS && !SLOW_OK.test(res.config.url || "") && !justResumed()) markBusy();
      return res;
    },
    async (err) => {
      const cfg = err.config;
      if (cfg && isBusyError(err)) {
        markBusy();
        // Read requests are retried quietly up to twice with backoff.
        if ((cfg.method || "get").toLowerCase() === "get" && (cfg.__busyRetry || 0) < 2) {
          cfg.__busyRetry = (cfg.__busyRetry || 0) + 1;
          await wait(1500 * cfg.__busyRetry + Math.random() * 1000);
          return axios(cfg);
        }
      }
      return Promise.reject(err);
    },
  );
};

const TXT = {
  de: ["Viel los gerade!", "Gerade sind sehr viele Nutzer gleichzeitig online. Bitte hab einen Moment Geduld, die Inhalte werden automatisch nachgeladen."],
  en: ["It's busy right now!", "Lots of people are online at the same time. Please bear with us for a moment, the content will reload automatically."],
  it: ["C'è molto traffico!", "In questo momento molte persone sono online contemporaneamente. Abbi un attimo di pazienza, i contenuti si ricaricano automaticamente."],
  tr: ["Şu an çok yoğun!", "Şu anda çok sayıda kişi aynı anda çevrimiçi. Lütfen biraz sabret, içerikler otomatik olarak yeniden yüklenecek."],
  ru: ["Сейчас большая нагрузка!", "Сейчас онлайн очень много людей одновременно. Пожалуйста, немного подожди — содержимое загрузится автоматически."],
  es: ["¡Hay mucho movimiento!", "Ahora mismo hay muchas personas conectadas a la vez. Ten un momento de paciencia, el contenido se recargará automáticamente."],
  fr: ["Beaucoup d'affluence !", "Beaucoup de personnes sont connectées en même temps. Merci de patienter un instant, le contenu se recharge automatiquement."],
  pl: ["Duży ruch!", "W tej chwili online jest bardzo wiele osób jednocześnie. Prosimy o chwilę cierpliwości, treści załadują się automatycznie."], sr: ["Тренутно је гужва!", "Много људи је истовремено онлајн. Молимо те за стрпљење, садржај ће се аутоматски поново учитати."], hr: ["Trenutno je gužva!", "Puno ljudi je online u isto vrijeme. Samo malo strpljenja, sadržaj će se automatski ponovno učitati."], sq: ["Ka shumë ngarkesë tani!", "Shumë persona janë online njëkohësisht. Të lutem prit pak, përmbajtja do të ringarkohet automatikisht."], sl: ["Trenutno je precej zasedeno!", "Veliko ljudi je hkrati online. Prosim, potrpi trenutek – vsebina se bo samodejno ponovno naložila."], pt: ["Está tudo a bombar agora!", "Há muita gente online ao mesmo tempo. Aguenta só um instante, o conteúdo vai recarregar automaticamente."], cs: ["Teď je tu rušno!", "Hodně lidí je online ve stejnou dobu. Vydrž chvilku, obsah se automaticky znovu načte."], hu: ["Most elég nagy a forgalom!", "Sokan vannak online egyszerre. Kérjük, várj egy pillanatot, a tartalom automatikusan újratölt."], hi: ["अभी थोड़ा व्यस्त है!", "काफ़ी लोग एक साथ ऑनलाइन हैं। थोड़ा इंतज़ार करें, कंटेंट अपने-आप फिर से लोड हो जाएगा।"], ms: ["Sekarang sedang sibuk!", "Ramai orang online serentak. Sabar sekejap ya, kandungan akan reload secara automatik."], nl: ["Het is nu erg druk!", "Veel mensen zijn tegelijk online. Even geduld alsjeblieft, de content laadt automatisch opnieuw."], zh: ["现在有点忙！", "同时在线的人很多。请稍等一下，内容会自动重新加载。"]
};

export const BusyBanner = ({ lang: langOverride }) => {
  const { lang: appLang } = useApp() || {};
  const lang = langOverride || appLang;
  const [title, text] = TXT[lang] || TXT.en;
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const fn = () => setBusy(true);
    listeners.add(fn);
    const t = setInterval(() => { if (Date.now() > busyUntil) setBusy(false); }, 2000);
    return () => { listeners.delete(fn); clearInterval(t); };
  }, []);
  if (!busy) return null;
  return (
    <div translate="no" className="fixed top-3 inset-x-0 z-[100] flex justify-center px-3 pointer-events-none" role="status" data-testid="busy-banner">
      <div className="pointer-events-auto w-full max-w-md animate-in fade-in slide-in-from-top-2 flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-50 dark:bg-amber-950/80 text-amber-900 dark:text-amber-100 px-4 py-3 shadow-lg backdrop-blur">
        <Hourglass className="h-5 w-5 shrink-0 mt-0.5 animate-pulse" />
        <div className="text-sm leading-snug">
          <strong className="block">{title}</strong>
          {text}
        </div>
      </div>
    </div>
  );
};
